import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { select, Store } from '@ngrx/store';
import { IAppState } from '../../store/app.state';
import { selectServiciosList } from '../../store/selectors/servicios.selectors';
import { IServicio, ICreateServicio, IUpdateServicio } from '../../interfaces/servicios.interface';
import { Actions, ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs/operators';
import { DownloadService } from 'src/app/services/download.service';
import { Table } from 'primeng/table';
import {
  CreateServicio, CreateServicioSuccess, DeleteServicio, DeleteServicioSuccess,
  GetServicios, UpdateServicio, UpdateServicioSuccess
} from '../../store/actions/servicios.actions';
import { selectProgramasList } from 'src/app/store/selectors/programas.selectors';
import { selectCentrosList } from 'src/app/store/selectors/centros.selectors';
import { GetProgramas } from 'src/app/store/actions/programas.actions';
import { GetCentros } from 'src/app/store/actions/centros.actions';
import { selectGruposList } from 'src/app/store/selectors/grupos.selectors';
import { combineLatest } from 'rxjs';
import { GetGrupos } from 'src/app/store/actions/grupos.actions';
import { ExportExcelService } from 'src/app/services/export-excel.service';

@Component({
  selector: 'app-servicios',
  templateUrl: './servicios.component.html',
  styleUrls: ['./servicios.component.scss']
})
export class ServiciosComponent implements OnInit, OnDestroy {
  @ViewChild('dt', { static: false }) table: Table;

  combined = combineLatest([
    this.store$.select(selectServiciosList),
    this.store$.select(selectGruposList)
  ]);

  servicios = this.combined.pipe(
    map(([servicios, grupos]) => {
      return servicios.map(servicio => {
        let numGrupos = 0;

        for(const grupo of grupos) {
          if(servicio.tipo === grupo.tipo_servicio) {
            numGrupos = numGrupos + 1;
          }
        }

        this.serviciosExcel.push(servicio);

        return {
          ...servicio,
          grupos_inscritos: numGrupos || 0
        }
      })
    })
  );

  grupos = this.store$.pipe(
    select(selectGruposList)
  );

  programas = this.store$.pipe(
    select(selectProgramasList),
    map(programas => programas.map(programa => {
      return { label: programa.programa_social, value: programa.programa_social }
    }))
  );

  centros = this.store$.pipe(
    select(selectCentrosList),
    map(centros => centros.map(centro => {
      return { label: centro.nombre, value: centro.nombre }
    }))
  );

  cols: any[];
  mode = '';
  header = '';
  displayForm = false;
  displayMap = false;
  mapOptions: any;
  mapOverlays= [];
  formServicio: FormGroup;
  serviciosExcel = [];
  dataForExcel = [];

  serviciosDrop = [
    {label: 'Alfabetización digital', value: 'Alfabetización digital'},
    {label: 'Proyecto de vida', value: 'Proyecto de vida'},
    {label: 'Educación financiera', value: 'Educación financiera'},
    {label: 'Educación en emprendimiento', value: 'Educación en emprendimiento'},
    {label: 'Talleres de oficio', value: 'Talleres de oficio'},
    {label: 'Mentoría', value: 'Mentoría'},
    {label: 'Asesoría en negocios', value: 'Asesoría en negocios'},
    {label: 'Capital semilla', value: 'Capital semilla'},
    {label: 'Vinculación al mercado', value: 'Vinculación al mercado'},
    {label: 'Vinculación a trabajo formal', value: 'Vinculación a trabajo formal'},
  ];

  downloadItems = [
    {label: 'CSV', command: () => { this.downloadService.exportCSV('servicios', this.table)}},
    {label: 'PDF', command: () => { this.downloadService.exportPdf('servicios', this.table)}},
  ];

  formSubmitSubscription = this.actions$.pipe(
    ofType(UpdateServicioSuccess, DeleteServicioSuccess, CreateServicioSuccess),
    tap(_ => {
      this.store$.dispatch(GetServicios());
      this.displayForm = false;
    })
  ).subscribe();

  constructor(
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private store$: Store<IAppState>,
    private actions$: Actions,
    public downloadService: DownloadService,
    private ete: ExportExcelService
  ) {
    this.formServicio = this.formBuilder.group({
      _id: new FormControl(''),
      nombre: new FormControl(''),
      tipo: new FormControl(''),
      descripcion: new FormControl(''),
      programa: new FormControl(''),
      centro: new FormControl(''),
      grupos: new FormControl(''),
      ubicacion: new FormControl(''),
      contacto: new FormControl(''),
      grupos_inscritos: new FormControl('')
    });
  }

  async ngOnInit() {
    this.cols = [
      { field: 'nombre', header: 'Nombre', exportable: true },
      { field: 'tipo', header: 'Servicio ofrecido', exportable: true },
      { field: 'programa', header: 'Programa Social', exportable: true },
      { field: 'centro', header: 'Centro de Aprendizaje', exportable: true },
      { field: 'grupos_inscritos', header: 'Grupos Inscritos', exportable: true },
      { field: 'acciones', header: 'Acciones', exportable: false }
    ];

    this.store$.dispatch(GetServicios());
    this.store$.dispatch(GetProgramas());
    this.store$.dispatch(GetCentros());
    this.store$.dispatch(GetGrupos());
  }

  ngOnDestroy() {
    this.formSubmitSubscription.unsubscribe();
  }

  editarServicio(servicio): void {
    this.mode = 'EDITAR'
    this.header = 'Editar Servicio';
    this.formServicio.patchValue({
      ...servicio
    });
    this.formServicio.enable();

    this.displayForm = true;
  }

  verServicio(servicio): void {
    this.mode = 'DETALLE'
    this.header = 'Detalle Servicio'
    this.formServicio.patchValue({
      ...servicio
    });
    this.formServicio.disable();

    this.displayForm = true;
  }

  agregarServicio() {
    this.mode = 'AGREGAR'
    this.header = 'Agregar Servicio'
    this.formServicio.patchValue({
      _id: '',
      nombre: '',
      tipo: '',
      descripcion: '',
      programa: '',
      centro: '',
      grupos: '',
      ubicacion: '',
      contacto: '',
      grupos_inscritos: ''
    })
    this.formServicio.enable();

    this.displayForm = true;
  }

  borrarServicio(servicio: IServicio) {
    this.confirmationService.confirm({
      header: 'Borrar Servicio',
      message: `¿Seguro que quiere borrar el servicio '${servicio.nombre}'?`,
      acceptLabel: 'Aceptar',
      rejectVisible: false,
      accept: async () => {
        this.store$.dispatch(DeleteServicio({id: servicio._id}))
      }
    });
  }

  // verMapa(servicio: IServicio) {
  //   this.mapOptions = {
  //     center: {lat: +servicio.latitud, lng: +servicio.longitud},
  //     zoom: 15
  //   }

  //   this.mapOverlays = [
  //     new google.maps.Marker({position: {lat:  +servicio.latitud, lng: +servicio.longitud}, title: servicio.nombre}),
  // ];

  //   this.displayMap = true;
  // }


  submitForm() {
    const form: IServicio = this.formServicio.getRawValue();

    if (this.mode === 'AGREGAR') {
      const payload: ICreateServicio = {
        ...form,
      }

      this.store$.dispatch(CreateServicio({servicio: payload}));
    } else if (this.mode === 'EDITAR') {
      const payload: IUpdateServicio = {
        ...form,
      }

      this.store$.dispatch(UpdateServicio({id: form._id, servicio: payload}));
    }

    return;
  }

  exportToExcel() {
    this.dataForExcel = [];

    const uniqueArray = this.serviciosExcel.filter((value, index) => {
      const _value = JSON.stringify(value);
      return index === this.serviciosExcel.findIndex(obj => {
        return JSON.stringify(obj) === _value;
      });
    });

    uniqueArray.forEach((row: any) => {
      const insert = { ...row }
      this.dataForExcel.push(Object.values(insert))
    })

    const reportData = {
      title: 'Servicios',
      data: this.dataForExcel,
      headers: Object.keys(this.serviciosExcel[0])
    }

    this.ete.exportExcel(reportData, false);
  }
}
